import React, { useCallback, useEffect, useRef, useState } from "react";
import { completeCommonBlock, getCommonDivaRanking, submitCommonDivaRanking } from "../service/api";
import { commonErrorMessage, newClientResponseId } from "./commonSubmission";
import { normalizeCommonMovie } from "./CommonMovieCard";
import { PairwisePoster } from "./PairwiseMoviePresentation";

const DRAFT_VERSION = 1;
const draftKey = (sessionId) => `commonRankingDraftV1:${sessionId}`;

export function moveTier(tiers, index, delta) {
  const target = index + delta;
  if (target < 0 || target >= tiers.length) return tiers;
  const next = tiers.map((tier) => [...tier]);
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

export function reorderTier(tiers, from, to) {
  if (from < 0 || from >= tiers.length || to < 0 || to >= tiers.length || from === to) return tiers;
  const next = tiers.map((tier) => [...tier]);
  next.splice(to, 0, next.splice(from, 1)[0]);
  return next;
}

export function mergeTier(tiers, index, delta) {
  const target = index + delta;
  if (target < 0 || target >= tiers.length) return tiers;
  const next = tiers.map((tier) => [...tier]);
  next[target] = delta < 0 ? [...next[target], ...next[index]] : [...next[index], ...next[target]];
  next.splice(index, 1);
  return next;
}

export function splitMovie(tiers, tierIndex, movieIndex) {
  if (tiers[tierIndex]?.length < 2) return tiers;
  const next = tiers.map((tier) => [...tier]);
  const [movieId] = next[tierIndex].splice(movieIndex, 1);
  next.splice(tierIndex + 1, 0, [movieId]);
  return next;
}

function validDraft(draft, movieIds) {
  if (draft?.version !== DRAFT_VERSION || !Array.isArray(draft.tiers)) return false;
  const flat = draft.tiers.flat();
  return draft.tiers.length >= 1
    && draft.tiers.every((tier) => Array.isArray(tier) && tier.length >= 1)
    && flat.length === 7 && new Set(flat).size === 7
    && flat.every((id) => movieIds.includes(id))
    && [1, 2].includes(draft.step)
    && (draft.approvalCutoff === "" || (Number.isInteger(Number(draft.approvalCutoff)) && Number(draft.approvalCutoff) >= 0 && Number(draft.approvalCutoff) <= draft.tiers.length))
    && (draft.confidence === "" || ["1", "2", "3", "4", "5"].includes(draft.confidence));
}

export default function CommonDivaRankingTask({ userId, commonSessionId, onPhaseChange }) {
  const [movies, setMovies] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [step, setStep] = useState(1);
  const [confidence, setConfidence] = useState("");
  const [approvalCutoff, setApprovalCutoff] = useState("");
  const [divaProtocolVersion, setDivaProtocolVersion] = useState("lean_cutoff_v2");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [retryPayload, setRetryPayload] = useState(null);
  const [announcement, setAnnouncement] = useState("");
  const [profileRevision, setProfileRevision] = useState(0);
  const draggedGroup = useRef(null);
  const headingRef = useRef(null);
  const timingRef = useRef({ step1Ms: 0, step2Ms: 0, activeAt: null, activeStep: 1 });
  const draftStateRef = useRef(null);
  const completedRef = useRef(false);
  draftStateRef.current = { tiers, step, approvalCutoff, confidence, profileRevision };

  const persistDraft = useCallback(() => {
    if (completedRef.current || !draftStateRef.current?.tiers.length) return;
    localStorage.setItem(draftKey(commonSessionId), JSON.stringify({ version: DRAFT_VERSION,
      ...draftStateRef.current, timing: { step1Ms: timingRef.current.step1Ms, step2Ms: timingRef.current.step2Ms } }));
  }, [commonSessionId]);
  const pauseTiming = useCallback(() => {
    const clock = timingRef.current;
    if (clock.activeAt !== null) {
      const elapsed = Math.max(0, Math.min(86400000, Date.now() - clock.activeAt));
      if (clock.activeStep === 1) clock.step1Ms = Math.min(86400000, clock.step1Ms + elapsed);
      else clock.step2Ms = Math.min(86400000, clock.step2Ms + elapsed);
      clock.activeAt = null;
    }
    persistDraft();
  }, [persistDraft]);
  const resumeTiming = useCallback((nextStep) => {
    timingRef.current.activeStep = nextStep;
    timingRef.current.activeAt = document.hidden ? null : Date.now();
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getCommonDivaRanking({ userId, commonSessionId });
      if (response.profile && response.phase !== "common_complete") {
        const completed = await completeCommonBlock({ userId, commonSessionId });
        localStorage.removeItem(draftKey(commonSessionId));
        onPhaseChange(completed.phase, commonSessionId);
        return;
      }
      if (response.phase === "common_complete" && (!response.profile || !response.canRevise)) {
        onPhaseChange("common_complete", commonSessionId);
        return;
      }
      const ids = response.movies.map((movie) => movie.imdb_tconst);
      let draft = null;
      try { draft = JSON.parse(localStorage.getItem(draftKey(commonSessionId))); } catch { /* discard invalid local draft */ }
      if (!validDraft(draft, ids) || (draft?.profileRevision ?? 0) !== (response.profileRevision ?? 0)) draft = null;
      const profile = response.profile;
      timingRef.current = { step1Ms: draft?.timing?.step1Ms ?? 0, step2Ms: draft?.timing?.step2Ms ?? 0, activeAt: null, activeStep: draft?.step ?? (profile ? 2 : 1) };
      resumeTiming(draft?.step ?? (profile ? 2 : 1));
      setMovies(response.movies);
      setTiers(draft?.tiers ?? profile?.tiers ?? ids.map((id) => [id]));
      setStep(draft?.step ?? (profile ? 2 : 1));
      setApprovalCutoff(draft?.approvalCutoff ?? (profile?.approvalCutoffAfterTieGroup == null ? "" : String(profile.approvalCutoffAfterTieGroup)));
      setConfidence(draft?.confidence ?? (profile?.rankingConfidence == null ? "" : String(profile.rankingConfidence)));
      setProfileRevision(response.profileRevision ?? 0);
      setDivaProtocolVersion(response.divaProtocolVersion ?? "lean_cutoff_v2");
      setError("");
    } catch (requestError) { setError(commonErrorMessage(requestError)); }
    finally { setLoading(false); }
  }, [commonSessionId, onPhaseChange, resumeTiming, userId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (movies.length === 7) headingRef.current?.focus(); }, [step, movies.length]);
  useEffect(() => {
    if (movies.length !== 7 || tiers.length === 0) return;
    persistDraft();
  }, [movies, tiers, step, approvalCutoff, confidence, commonSessionId, profileRevision, persistDraft]);
  useEffect(() => {
    const onVisibility = () => { if (document.hidden) pauseTiming(); else resumeTiming(draftStateRef.current?.step ?? 1); };
    window.addEventListener("pagehide", pauseTiming);
    document.addEventListener("visibilitychange", onVisibility);
    return () => { pauseTiming(); window.removeEventListener("pagehide", pauseTiming); document.removeEventListener("visibilitychange", onVisibility); };
  }, [pauseTiming, resumeTiming]);

  const changeStep = (nextStep) => {
    pauseTiming();
    resumeTiming(nextStep);
    setStep(nextStep);
    setError("");
  };

  const movieById = new Map(movies.map((movie) => [movie.imdb_tconst, normalizeCommonMovie(movie)]));
  const isLean = divaProtocolVersion === "lean_cutoff_v2";
  const changeTiers = (next, message) => {
    if (next === tiers || JSON.stringify(next) === JSON.stringify(tiers)) return;
    setTiers(next);
    setApprovalCutoff("");
    setConfidence("");
    setRetryPayload(null);
    setAnnouncement(message);
    setError("");
  };

  const finish = async (payload) => {
    setSubmitting(true); setError("");
    try {
      await submitCommonDivaRanking(payload);
      setRetryPayload(null);
      const completed = await completeCommonBlock({ userId, commonSessionId });
      completedRef.current = true;
      localStorage.removeItem(draftKey(commonSessionId));
      onPhaseChange(completed.phase, commonSessionId);
    } catch (requestError) { resumeTiming(2); setRetryPayload(payload); setError(commonErrorMessage(requestError)); }
    finally { setSubmitting(false); }
  };

  const submit = (event) => {
    event.preventDefault();
    if (!confidence || (isLean && approvalCutoff === "")) {
      setError("Please choose an approval line and ranking confidence before continuing.");
      return;
    }
    pauseTiming();
    finish({ userId, commonSessionId, tiers,
      ...(isLean ? { approvalCutoffAfterTieGroup: Number(approvalCutoff) } : {}),
      rankingConfidence: Number(confidence), clientResponseId: newClientResponseId(),
      rankingStep1ElapsedMs: timingRef.current.step1Ms,
      rankingStep2ElapsedMs: timingRef.current.step2Ms });
  };

  const rankingGroup = (tier, tierIndex, editable) => (
    <div role="listitem" data-testid="ranking-group" className={`ranking-board-group${tier.length > 1 ? " ranking-board-group--tied" : ""}`} key={tier.join("-")}
      draggable={editable} onDragStart={editable ? (event) => { draggedGroup.current = tierIndex; event.dataTransfer.effectAllowed = "move"; event.dataTransfer.setData("text/plain", String(tierIndex)); } : undefined}
      onDragOver={editable ? (event) => event.preventDefault() : undefined}
      onDrop={editable ? (event) => { event.preventDefault(); if (draggedGroup.current !== null) changeTiers(reorderTier(tiers, draggedGroup.current, tierIndex), `Moved rank ${draggedGroup.current + 1} to position ${tierIndex + 1}.`); draggedGroup.current = null; } : undefined}
      onDragEnd={editable ? () => { draggedGroup.current = null; } : undefined}>
      {tier.length > 1 && <div className="ranking-tie-heading">Rank {tierIndex + 1} · Same rank</div>}
      {tier.map((movieId, movieIndex) => {
        const movie = movieById.get(movieId);
        return <div data-testid="ranking-card" className="ranking-board-card" key={movieId}>
          <span className="ranking-rank-number" aria-label={`Rank ${tierIndex + 1}`}>{tierIndex + 1}</span>
          <PairwisePoster movie={{ ...movie, posterPath: movie.poster }} width={52} height={78} />
          <span className="ranking-movie-identity"><strong>{movie.title}</strong><span>{movie.year}</span></span>
          {editable && <>
            <span className="ranking-drag-handle" aria-hidden="true">⋮⋮ Drag</span>
            <span className="ranking-board-controls">
              <button type="button" aria-label={`Move ${movie.title}${tier.length > 1 ? " and its tied rank" : ""} up`} disabled={tierIndex === 0} onClick={() => changeTiers(moveTier(tiers, tierIndex, -1), `Moved ${movie.title} up.`)}>↑</button>
              <button type="button" aria-label={`Move ${movie.title}${tier.length > 1 ? " and its tied rank" : ""} down`} disabled={tierIndex === tiers.length - 1} onClick={() => changeTiers(moveTier(tiers, tierIndex, 1), `Moved ${movie.title} down.`)}>↓</button>
              {tier.length > 1 && <button type="button" className="ranking-separate" onClick={() => changeTiers(splitMovie(tiers, tierIndex, movieIndex), `Separated ${movie.title} from the tie.`)}>Separate {movie.title} from tie</button>}
            </span>
          </>}
        </div>;
      })}
    </div>
  );

  const approvalBoundary = (index) => {
    const selected = approvalCutoff === String(index);
    const position = index === 0 ? "before all movies" : index === tiers.length ? "after all movies" : `after rank ${index}`;
    return <div className={`ranking-approval-slot${selected ? " ranking-approval-slot--selected" : ""}`} key={`cutoff-${index}`}>
      <button type="button" aria-pressed={selected} aria-label={`Place approval line ${position}`} onClick={() => { setApprovalCutoff(String(index)); setError(""); }}>
        {selected ? "— APPROVAL LINE —" : "Place approval line here"}
      </button>
      {selected && index < tiers.length && <span className="ranking-approval-side">WOULD NOT CONSIDER</span>}
    </div>;
  };

  if (loading && !movies.length) return <p role="status">Loading the ranking task…</p>;
  if (!movies.length) return <div className="common-error" role="alert">{error}<button type="button" onClick={load}>Try again</button></div>;
  return <section className="common-ranking">
    <p className="ranking-step-label">Step {step} of 2</p>
    <h2 ref={headingRef} tabIndex="-1">{step === 1 ? "Rank these movies" : "Review your ranking"}</h2>
    {step === 1 ? <>
      <p>Arrange the seven movies from the one you most prefer to the one you least prefer.</p>
      <p>The starting order is random and does not represent a ranking.</p>
      <p>Drag the movies to rearrange them, or use the arrow buttons.</p>
      <p>If you genuinely have no preference between two or more movies, place them at the same rank.</p>
      <p className="sr-only" aria-live="polite">{announcement}</p>
      <div className="ranking-board" role="list" aria-label="Your movie ranking">
        {tiers.map((tier, index) => <React.Fragment key={tier.join("-")}>
          {rankingGroup(tier, index, true)}
          {index < tiers.length - 1 && <div className="ranking-same-rank-slot"><button type="button" aria-label={`Same preference between ranks ${index + 1} and ${index + 2}`} onClick={() => changeTiers(mergeTier(tiers, index + 1, -1), `Joined ranks ${index + 1} and ${index + 2}.`)}>Same preference</button></div>}
        </React.Fragment>)}
      </div>
      {error && <div className="common-error" role="alert">{error}</div>}
      <div className="ranking-navigation ranking-navigation--forward"><button type="button" className="submit-button" onClick={() => changeStep(2)}>Continue ➡</button></div>
    </> : <form onSubmit={submit}>
      <p>Review the ranking you created. Use Back to edit the order or ties.</p>
      {isLean && <div className="ranking-approval-question"><h3>Which of these movies would you genuinely consider choosing?</h3><p>Place the line below the lowest-ranked movie you would still consider choosing.</p></div>}
      {isLean && approvalCutoff !== "" && approvalCutoff !== "0" && <p className="ranking-approval-side">WOULD CONSIDER</p>}
      <div className="ranking-board ranking-board--review" role="list" aria-label="Ranking and approval boundary">
        {isLean && approvalBoundary(0)}
        {tiers.map((tier, index) => <React.Fragment key={tier.join("-")}>
          {rankingGroup(tier, index, false)}
          {isLean && approvalBoundary(index + 1)}
        </React.Fragment>)}
      </div>
      <fieldset className="ranking-confidence"><legend>How confident are you that this ranking reflects your general movie preferences?</legend>
        <div className="ranking-confidence-options">{[1, 2, 3, 4, 5].map((value) => <label key={value}><input type="radio" name="rankingConfidence" value={value} checked={confidence === String(value)} onChange={() => setConfidence(String(value))} /><span>{value}</span></label>)}</div>
        <div className="scale-labels"><span>1 = Not at all confident</span><span>5 = Extremely confident</span></div>
      </fieldset>
      <p>Review your ranking and approval line before continuing.</p>
      {error && <div className="common-error" role="alert">{error}</div>}
      <div className="ranking-navigation">
        <button type="button" className="back-button" disabled={submitting || Boolean(retryPayload)} onClick={() => changeStep(1)}>⬅ Back</button>
        {retryPayload ? <button type="button" className="submit-button" disabled={submitting} onClick={() => finish(retryPayload)}>Retry the same submission</button>
          : <button type="submit" className="submit-button" disabled={submitting}>Confirm and continue ➡</button>}
      </div>
    </form>}
  </section>;
}
